// pbo-conductor — 가로 카드 레일(CardRail). core.Flex 의 railX 축이 그리는 실물이며,
//  산출물에도 **이 파일 그대로** 실린다(동반 파일 — companionSources.ts).
//
// 왜 필요한가(LUA 2026-09-15 "해상도 줄면 운영카드가 아래로 내려가는데 좌우 스크롤 되게"):
//   카드 줄이 wrap 이면 폭이 모자랄 때 카드가 **아래로 접힌다**. 줄이 2단이 되는 순간
//   "한 줄 = 운영 목록"이라는 읽기 단위가 깨지고, 그 아래 주간 그리드까지 20px 씩 밀린다.
//   레일은 줄 수를 1로 고정하고 넘치는 만큼을 가로로 흘린다 — 세로 레이아웃이 해상도에 흔들리지 않는다.
//
// 왜 스크롤바가 아니라 [<][>] 인가(LUA "스크롤이 아니라 사실상 >버튼일듯"):
//   데스크톱 백오피스에서 가로 스크롤바는 발견되지 않는다(트랙패드 관성 스와이프가 기본이 아니고,
//   overlay 스크롤바는 호버 전엔 보이지 않는다). 넘침이 있다는 사실 자체를 **버튼의 존재**로 알린다.
//   그래서 버튼은 넘칠 때만 나타나고, 끝에 닿으면 그 방향 버튼이 사라진다 — 버튼 유무가 곧 잔량 표시다.
import { useCallback, useEffect, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
// 아이콘 = DS 공통 에셋(@musinsa/pbo-assets). viewBox 0 0 20 20 격자라 버튼 아이콘 박스와 짝이 맞는다.
//  color 는 반드시 명시한다 — DS BasicIcon 기본색이 '#000000' 이라 그대로 두면 부모 글자색을 안 따른다.
import { Icon as DsIcon } from '@musinsa/pbo-assets'
import './cardRail.css'

export interface CardRailProps {
  gap?: number
  align?: CSSProperties['alignItems']
  style?: CSSProperties
  className?: string
  children?: ReactNode
}

export function CardRail({ gap = 12, align = 'stretch', style, className, children }: CardRailProps) {
  const vp = useRef<HTMLDivElement>(null)
  const [nav, setNav] = useState({ prev: false, next: false })

  // 잔량 판정 — 양 끝 1px 여유는 소수 폭(스크롤 위치가 .5px 로 떨어지는 경우) 때문이다.
  const measure = useCallback(() => {
    const el = vp.current
    if (!el) return
    const max = el.scrollWidth - el.clientWidth
    setNav({ prev: el.scrollLeft > 1, next: max > 1 && el.scrollLeft < max - 1 })
  }, [])

  useEffect(() => {
    const el = vp.current
    if (!el) return
    measure()
    el.addEventListener('scroll', measure, { passive: true })
    // 카드가 늘거나(운영 추가) 창 폭이 바뀌면 다시 잰다 — 레일 자신과 자식 둘 다 본다.
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    Array.from(el.children).forEach((c) => ro.observe(c))
    return () => {
      el.removeEventListener('scroll', measure)
      ro.disconnect()
    }
  }, [measure, children])

  // 한 번에 넘기는 양 = **보이는 카드 수만큼**(카드 경계로 끊는다 — 카드가 반쯤 잘린 채 멈추면
  //  몇 개가 더 있는지 읽히지 않는다). 카드 폭은 첫 자식에서 실측한다(고정값을 박지 않기 위함).
  const page = (dir: 1 | -1) => {
    const el = vp.current
    if (!el) return
    const first = el.firstElementChild as HTMLElement | null
    const unit = (first?.getBoundingClientRect().width ?? 180) + gap
    const n = Math.max(1, Math.floor(el.clientWidth / unit))
    el.scrollBy({ left: dir * unit * n, behavior: 'smooth' })
  }

  return (
    <div
      className={['pbo-rail', nav.prev ? 'has-prev' : '', nav.next ? 'has-next' : '', className].filter(Boolean).join(' ')}
      style={style}
    >
      <div className="pbo-rail-vp" ref={vp} style={{ gap, alignItems: align }}>
        {children}
      </div>
      {nav.prev ? (
        <button type="button" className="pbo-rail-nav prev" aria-label="이전 보기" onClick={() => page(-1)}>
          <DsIcon name="ic-chevronLeft" size={16} color="currentColor" />
        </button>
      ) : null}
      {nav.next ? (
        <button type="button" className="pbo-rail-nav next" aria-label="다음 보기" onClick={() => page(1)}>
          <DsIcon name="ic-chevronRight" size={16} color="currentColor" />
        </button>
      ) : null}
    </div>
  )
}
